import React from 'react';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/CloseRounded';

import styles from 'src/components/labels/LabelChip.module.css';

/**
 * Legacy property labels without a stored color use this neutral grey rather
 * than the default lead-label green.
 */
export const PROPERTY_LABEL_CHIP_COLOR = 'rgb(117, 117, 117)';

/**
 * The backend stores label colors as bare hex strings (e.g. ``2196f3``);
 * prefix them with ``#`` so they are valid CSS. Colors in any other format
 * pass through unchanged.
 */
export function labelColorToCss(color: string | null | undefined): string | undefined {
    return color && /^[0-9a-f]{6}$/i.test(color) ? `#${color}` : color ?? undefined;
}

export type LabelChipProps = {
    name: string;
    /**
     * Pill color, applied via the ``--label-color`` CSS variable. When empty
     * the stylesheet falls back to the default (green) color.
     */
    color?: string;
    onDelete?: (event: React.MouseEvent<HTMLButtonElement>) => void;
    onClick?: () => void;
    className?: string;
    size?: 'compact' | 'body';
};

/**
 * The single label chip used everywhere: a color-tinted rounded pill with a
 * pin dot, the label name (ellipsised), and an optional delete button. Lead
 * and property labels render identically; the :func:`LeadLabels` and
 * :func:`PropertyLabels` containers wrap this to lay out a row of chips.
 */
export default function LabelChip({
    name,
    color,
    onDelete,
    onClick,
    className,
    size = 'compact',
}: LabelChipProps) {
    const isBodySize = size === 'body';

    return (
        <div
            className={`${styles.label} ${isBodySize ? styles.labelBody : ''} ${onClick ? styles.clickable : ''} ${className ?? ''}`}
            style={{ '--label-color': labelColorToCss(color) } as React.CSSProperties}
            onClick={onClick}
        >
            <div className={styles.pin} />
            <Typography variant="caption" className={`${styles.text} ${isBodySize ? styles.textBody : ''}`}>
                {name}
            </Typography>
            {!!onDelete && (
                <button
                    type="button"
                    className={styles.deleteButton}
                    data-testid="remove-label-button"
                    onClick={(event) => {
                        event.stopPropagation();
                        onDelete(event);
                    }}
                    aria-label={`Remove ${name}`}
                >
                    <CloseIcon className={styles.deleteIcon} />
                </button>
            )}
        </div>
    );
}
